import { useState, useEffect, useCallback } from 'react';
import './InfrastructureWidget.css';

/*
 * InfrastructureWidget — Live status of all ABET platform services.
 * CSS prefix: inf-
 * Shows: GitHub, Cloudflare, HuggingFace, Vercel, Supabase, TigerData,
 *        Discord, PostgreSQL, DuckDB, QuestDB, MongoDB, Oracle VM
 */

const SERVICES = [
  {
    id: 'github',
    name: 'GitHub',
    category: 'source',
    url: 'https://github.com/misterplusev',
    healthUrl: 'https://www.githubstatus.com/api/v2/status.json',
    description: '6 repos, CI/CD',
    color: '#24292f',
    logo: 'github',
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare Workers',
    category: 'compute',
    url: 'https://dash.cloudflare.com',
    healthUrl: null,
    checkUrls: [
      { label: 'TheOddsAPI Proxy', url: 'https://abet-theoddsapi-proxy.abetco.workers.dev/health' },
      { label: 'Receiver Proxy', url: 'https://abet-receiver-proxy.abetco.workers.dev/health' },
    ],
    description: '3 workers, 100K req/day',
    color: '#f48120',
    logo: 'cloudflare',
  },
  {
    id: 'huggingface',
    name: 'HuggingFace',
    category: 'compute',
    url: 'https://huggingface.co/abetco',
    healthUrl: null,
    checkUrls: [
      { label: 'MLB Monitors', url: 'https://abetco-mlb-monitors.hf.space/health' },
    ],
    description: '2 Spaces, Datasets, Models',
    color: '#ff9d00',
    logo: 'huggingface',
  },
  {
    id: 'vercel',
    name: 'Vercel',
    category: 'hosting',
    url: 'https://abet-dashboard.vercel.app',
    healthUrl: null,
    checkUrls: [
      { label: 'Dashboard', url: 'https://abet-dashboard.vercel.app' },
    ],
    description: 'Dashboard hosting, auto-deploy',
    color: '#000000',
    logo: 'vercel',
  },
  {
    id: 'supabase',
    name: 'Supabase',
    category: 'database',
    url: 'https://supabase.com/dashboard',
    healthUrl: null,
    checkUrls: [
      { label: 'REST API', url: 'https://kiujpszjsszqzinyxkoi.supabase.co/rest/v1/' },
    ],
    description: '500MB, live odds + auth',
    color: '#3ecf8e',
    logo: 'supabase',
  },
  {
    id: 'tigerdata',
    name: 'TigerData',
    category: 'database',
    url: 'https://console.cloud.timescale.com',
    description: '2 svc x 750MB, MLB predictions',
    color: '#fdb515',
    logo: 'tigerdata',
  },
  {
    id: 'postgresql',
    name: 'PostgreSQL',
    category: 'database',
    url: null,
    description: '11.8GB local, 45K games, 2010-2025',
    color: '#336791',
    logo: 'postgresql',
    isLocal: true,
  },
  {
    id: 'duckdb',
    name: 'DuckDB',
    category: 'database',
    url: null,
    description: 'Parquet queries, feature store',
    color: '#fff200',
    logo: 'duckdb',
    isLocal: true,
  },
  {
    id: 'questdb',
    name: 'QuestDB',
    category: 'database',
    url: null,
    description: 'Time-series DB, high-freq ingest',
    color: '#d14671',
    logo: 'questdb',
  },
  {
    id: 'mongodb',
    name: 'MongoDB',
    category: 'database',
    url: null,
    description: 'Document store, unstructured data',
    color: '#00ed64',
    logo: 'mongodb',
  },
  {
    id: 'discord',
    name: 'Discord',
    category: 'notifications',
    url: null,
    description: 'Webhooks: predictions, alerts, system',
    color: '#5865f2',
    logo: 'discord',
  },
  {
    id: 'oracle',
    name: 'Oracle Cloud VM',
    category: 'compute',
    url: null,
    healthUrl: null,
    checkUrls: [
      { label: 'Receiver', url: 'https://abet-receiver-proxy.abetco.workers.dev/health' },
    ],
    description: 'AMD, 39 scrapers, receiver',
    color: '#c74634',
    logo: 'oracle',
  },
];

const CATEGORIES = [
  { id: 'source', label: 'Source Control', icon: '\u{1F4BB}' },
  { id: 'compute', label: 'Compute', icon: '\u{2699}\uFE0F' },
  { id: 'database', label: 'Databases', icon: '\u{1F5C4}\uFE0F' },
  { id: 'hosting', label: 'Hosting', icon: '\u{1F310}' },
  { id: 'notifications', label: 'Notifications', icon: '\u{1F514}' },
];

// Service logo SVGs (inline to avoid external deps)
const LOGOS = {
  github: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
    </svg>
  ),
  cloudflare: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <path d="M16.51 15.78c.23-.8.1-1.54-.37-2.07-.44-.49-1.12-.78-1.87-.81L8.5 12.74c-.08 0-.14-.04-.17-.1a.2.2 0 01.01-.19c.04-.07.11-.12.19-.13l5.84-.18c1.23-.04 2.57-.99 3.07-2.18l.64-1.52a.36.36 0 00.02-.2 5.79 5.79 0 00-11.16-.36c-.71-.5-1.6-.73-2.52-.56a3.32 3.32 0 00-2.67 2.75 3.6 3.6 0 00.04 1.2A4.46 4.46 0 00.02 15.6a4.4 4.4 0 004.3 3.56h11.59c.09 0 .17-.06.2-.15l.4-1.36z"/>
      <path d="M18.84 11.15l-.14.01a.18.18 0 00-.16.13l-.27.93c-.23.8-.1 1.54.37 2.07.44.49 1.12.78 1.87.81l1.07.04c.08 0 .15.04.18.11.03.07.03.14-.01.2-.04.07-.11.12-.19.13l-1.14.04c-1.24.04-2.58.99-3.08 2.18l-.17.41c-.03.08.02.16.1.16h5.65A3.58 3.58 0 0024 15.6a4.46 4.46 0 00-4.42-4.47c-.25 0-.5.02-.74.05z"/>
    </svg>
  ),
  huggingface: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="8.5" cy="10" r="1.5"/>
      <circle cx="15.5" cy="10" r="1.5"/>
      <path d="M7 14.5c0 0 2 3 5 3s5-3 5-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  vercel: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <path d="M12 1L24 22H0L12 1z"/>
    </svg>
  ),
  supabase: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <path d="M13.7 21.8c-.4.5-1.3.2-1.3-.5V13h8.4c.8 0 1.2.9.7 1.5L13.7 21.8z" opacity="0.6"/>
      <path d="M10.3 2.2c.4-.5 1.3-.2 1.3.5V11H3.2c-.8 0-1.2-.9-.7-1.5L10.3 2.2z"/>
    </svg>
  ),
  tigerdata: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <rect x="3" y="3" width="18" height="18" rx="3" fill="none" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M7 8h10M7 12h10M7 16h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  postgresql: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <path d="M17.5 3.5c-1.7-1.2-3.8-1.7-5.9-1.5-2.1.2-3.9 1-5.2 2.3C5 5.6 4.2 7.3 3.9 9.2c-.3 1.9 0 3.9 1 5.5.3.5.3 1.1.1 1.6l-1.2 3.3c-.1.3.2.6.5.5l3.3-1.2c.5-.2 1.1-.2 1.6.1 1.3.7 2.8 1.1 4.3 1.1 2.3 0 4.5-.9 6.1-2.5 1.6-1.6 2.5-3.8 2.5-6.1 0-3.1-1.7-6-4.6-8z"/>
    </svg>
  ),
  duckdb: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <ellipse cx="12" cy="12" rx="10" ry="8" fill="none" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="9" cy="10" r="1.2"/>
      <path d="M14 11c0 0 1.5 0 2.5 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  questdb: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
      <path d="M15 12l-4-3v6l4-3z"/>
      <line x1="16" y1="16" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  mongodb: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <path d="M12.5 2.1c-.3-.4-.5-.7-.6-1.1-.1.4-.3.7-.6 1.1C9.5 4.7 4 9.3 4 14.5 4 19 7.6 22 12 22s8-3 8-7.5c0-5.2-5.5-9.8-7.5-12.4zm-.4 18c-.2 0-.3-.1-.4-.2-.6-.5-1-1.2-1.2-2 0-.1 0-.2.1-.3.1-.1.2-.1.3 0 .4.2.8.3 1.2.3.4 0 .8-.1 1.2-.3.1-.1.2 0 .3 0 .1.1.1.2.1.3-.2.8-.6 1.5-1.2 2-.1.1-.2.2-.4.2z"/>
    </svg>
  ),
  discord: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <path d="M20.32 4.37a19.8 19.8 0 00-4.93-1.51.07.07 0 00-.08.04c-.21.38-.45.87-.61 1.26a18.27 18.27 0 00-5.4 0 12.6 12.6 0 00-.62-1.26.07.07 0 00-.08-.04 19.74 19.74 0 00-4.93 1.51.07.07 0 00-.03.03C1.11 8.39.34 12.27.75 16.1a.08.08 0 00.03.05 19.9 19.9 0 005.99 3.03.07.07 0 00.08-.03c.46-.63.87-1.3 1.22-2a.07.07 0 00-.04-.1 13.1 13.1 0 01-1.87-.9.07.07 0 01-.01-.12c.13-.09.25-.19.37-.29a.07.07 0 01.07-.01c3.93 1.79 8.18 1.79 12.07 0a.07.07 0 01.07.01c.12.1.25.2.37.29a.07.07 0 01-.01.12c-.6.35-1.22.65-1.87.9a.07.07 0 00-.04.1c.36.7.77 1.37 1.22 2a.07.07 0 00.08.03 19.83 19.83 0 006-3.03.08.08 0 00.03-.05c.47-4.87-.79-9.1-3.34-12.84a.06.06 0 00-.03-.03zM8.02 13.71c-1.11 0-2.03-1.02-2.03-2.28s.9-2.28 2.03-2.28c1.14 0 2.04 1.03 2.03 2.28 0 1.26-.9 2.28-2.03 2.28zm7.5 0c-1.11 0-2.03-1.02-2.03-2.28s.9-2.28 2.03-2.28c1.14 0 2.04 1.03 2.03 2.28 0 1.26-.89 2.28-2.03 2.28z"/>
    </svg>
  ),
  oracle: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="inf-logo-svg">
      <path d="M7.2 4.8h9.6c3.98 0 7.2 3.22 7.2 7.2s-3.22 7.2-7.2 7.2H7.2C3.22 19.2 0 15.98 0 12s3.22-7.2 7.2-7.2zm9.37 11.7c2.49 0 4.5-2.01 4.5-4.5s-2.01-4.5-4.5-4.5H7.43c-2.49 0-4.5 2.01-4.5 4.5s2.01 4.5 4.5 4.5h9.14z"/>
    </svg>
  ),
};

function ServiceCard({ service, status }) {
  const statusClass = status === 'up' ? 'inf-status-up' :
                      status === 'down' ? 'inf-status-down' :
                      status === 'degraded' ? 'inf-status-degraded' :
                      'inf-status-unknown';

  const statusLabel = status === 'up' ? 'Operational' :
                      status === 'down' ? 'Down' :
                      status === 'degraded' ? 'Degraded' :
                      service.isLocal ? 'Local' : 'Unknown';

  return (
    <div className={`inf-card ${statusClass}`}>
      <div className="inf-card-header">
        <div className="inf-logo" style={{ color: service.color }}>
          {LOGOS[service.logo]}
        </div>
        <div className="inf-card-info">
          <div className="inf-card-name">{service.name}</div>
          <div className="inf-card-desc">{service.description}</div>
        </div>
        <div className={`inf-status-dot ${statusClass}`} title={statusLabel} />
      </div>
    </div>
  );
}

export default function InfrastructureWidget() {
  const [statuses, setStatuses] = useState({});
  const [lastCheck, setLastCheck] = useState(null);

  const checkHealth = useCallback(async () => {
    const newStatuses = {};

    for (const svc of SERVICES) {
      if (svc.isLocal) {
        newStatuses[svc.id] = 'local';
        continue;
      }

      if (svc.healthUrl) {
        try {
          const res = await fetch(svc.healthUrl, { signal: AbortSignal.timeout(5000) });
          const data = await res.json();
          newStatuses[svc.id] = data?.status?.indicator === 'none' ? 'up' : 'degraded';
        } catch {
          newStatuses[svc.id] = 'unknown';
        }
      } else if (svc.checkUrls?.length) {
        try {
          const results = await Promise.allSettled(
            svc.checkUrls.map(u =>
              fetch(u.url, { signal: AbortSignal.timeout(5000), mode: 'no-cors' })
            )
          );
          const anyUp = results.some(r => r.status === 'fulfilled');
          newStatuses[svc.id] = anyUp ? 'up' : 'down';
        } catch {
          newStatuses[svc.id] = 'unknown';
        }
      } else {
        newStatuses[svc.id] = 'unknown';
      }
    }

    setStatuses(newStatuses);
    setLastCheck(new Date());
  }, []);

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 60000);
    return () => clearInterval(interval);
  }, [checkHealth]);

  const servicesByCategory = CATEGORIES.map(cat => ({
    ...cat,
    services: SERVICES.filter(s => s.category === cat.id),
  })).filter(cat => cat.services.length > 0);

  const upCount = Object.values(statuses).filter(s => s === 'up' || s === 'local').length;
  const totalCloud = SERVICES.filter(s => !s.isLocal).length;
  const checkedCloud = Object.values(statuses).filter(s => s === 'up').length;

  return (
    <div className="inf-container">
      <div className="inf-header">
        <h2 className="inf-title">Infrastructure</h2>
        <div className="inf-summary">
          <span className="inf-count-up">{checkedCloud}</span>
          <span className="inf-count-sep">/</span>
          <span className="inf-count-total">{totalCloud}</span>
          <span className="inf-count-label">cloud services up</span>
        </div>
        {lastCheck && (
          <div className="inf-last-check">
            Checked {lastCheck.toLocaleTimeString()}
          </div>
        )}
      </div>

      {servicesByCategory.map(cat => (
        <div key={cat.id} className="inf-category">
          <div className="inf-category-label">
            <span>{cat.icon}</span> {cat.label}
          </div>
          <div className="inf-grid">
            {cat.services.map(svc => (
              <ServiceCard
                key={svc.id}
                service={svc}
                status={statuses[svc.id] || 'unknown'}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
